"use client"

import { useEffect, useState, type ReactNode } from "react"

const APPLY_TARGET_ID = "apply-now"
const APPLY_NAVIGATION_FOCUS_ATTRIBUTE = "data-apply-navigation-focus"

interface ApplyNowLinkProps {
  children: ReactNode
  className: string
  onNavigate?: () => void
}

export function ApplyNowLink({
  children,
  className,
  onNavigate,
}: ApplyNowLinkProps) {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(APPLY_TARGET_ID)
    if (!target) return

    event.preventDefault()
    onNavigate?.()
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches
    target.scrollIntoView?.({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    })

    const firstField = target.querySelector<HTMLElement>(
      "[data-application-first-field]",
    )
    if (!firstField) {
      target.focus({ preventScroll: true })
      return
    }

    // Programmatic focus gives keyboard users a predictable landing point, but
    // should not redefine the application_started funnel event. The form
    // ignores this one navigation focus and measures the candidate's first
    // actual field interaction as before.
    firstField.setAttribute(APPLY_NAVIGATION_FOCUS_ATTRIBUTE, "true")
    firstField.focus({ preventScroll: true })
    window.setTimeout(() => {
      firstField.removeAttribute(APPLY_NAVIGATION_FOCUS_ATTRIBUTE)
    }, 0)
  }

  return (
    <a
      href="#apply-now"
      aria-describedby="application-effort-summary"
      className={className}
      onClick={handleClick}
    >
      {children}
    </a>
  )
}

export function StickyApplyBar({ jobTitle }: { jobTitle: string }) {
  const [formVisible, setFormVisible] = useState(false)

  useEffect(() => {
    const target = document.getElementById(APPLY_TARGET_ID)
    if (!target || !("IntersectionObserver" in window)) return

    const observer = new IntersectionObserver(
      ([entry]) => setFormVisible(entry.isIntersecting),
      { threshold: 0.1 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  if (formVisible) return null

  return (
    <aside
      aria-label="Application shortcut"
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 px-4 pt-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] lg:hidden"
    >
      <div className="mx-auto flex max-w-5xl items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{jobTitle}</p>
          <p className="text-xs text-muted-foreground">
            Apply in about 2 minutes
          </p>
        </div>
        <ApplyNowLink
          onNavigate={() => setFormVisible(true)}
          className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Apply now
        </ApplyNowLink>
      </div>
    </aside>
  )
}

export function isApplyNavigationFocus(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.hasAttribute(APPLY_NAVIGATION_FOCUS_ATTRIBUTE)
  )
}
